import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '../components/Button';
import { ChoiceGroup } from '../components/Choice';
import { Field, Input } from '../components/Field';
import { useDb } from '../db/hooks';
import { ACCESS_LABELS, saveProfile, useProfile, type AccessType, type Profile } from '../data/profile';
import { saveSetting, useSettings } from '../data/settings';
import { useSchedules } from '../data/schedule';
import { ScheduleForm } from '../features/schedule/ScheduleForm';
import { inputValue, optionalNumber, optionalPastDate, optionalText } from '../lib/forms';

const STEPS = ['About you', 'Dialysis', 'Fluid & weight', 'Access'] as const;

function StepShell({ step, title, intro, children }: { step: number; title: string; intro: string; children: React.ReactNode }) {
  return (
    <div className="safe-top mx-auto max-w-lg px-4 py-8">
      <ol className="mb-6 flex gap-1.5" aria-label={`Step ${step + 1} of ${STEPS.length}`}>
        {STEPS.map((label, index) => (
          <li key={label} className={`h-1.5 flex-1 rounded-full ${index <= step ? 'bg-clinical-primary' : 'bg-clinical-line'}`} title={label} />
        ))}
      </ol>
      <p className="text-xs font-semibold uppercase tracking-wide text-clinical-muted">
        Step {step + 1} of {STEPS.length}
      </p>
      <h1 className="mt-1 text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-1 text-sm text-clinical-muted">{intro}</p>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function SkipButton({ onClick, label = 'Skip this step' }: { onClick: () => void; label?: string }) {
  return (
    <Button type="button" variant="ghost" onClick={onClick}>
      {label}
    </Button>
  );
}

const nameSchema = z.object({
  full_name: z.string().trim().min(1, 'Enter your name, or skip this step.').max(120, 'Keep it under 120 characters.'),
  dob: optionalPastDate
});

function NameStep({ profile, onNext, onSkipAll }: { profile: Profile | null; onNext: () => void; onSkipAll: () => void }) {
  const db = useDb();
  const form = useForm<z.input<typeof nameSchema>, unknown, z.output<typeof nameSchema>>({
    resolver: zodResolver(nameSchema),
    defaultValues: { full_name: inputValue(profile?.full_name), dob: inputValue(profile?.dob) }
  });
  const { errors, isSubmitting } = form.formState;
  return (
    <StepShell step={0} title="Let’s set up RenalFlow" intro="A few basics so your log and doctor reports make sense. You can change all of this later.">
      <form
        noValidate
        className="space-y-4"
        onSubmit={form.handleSubmit(async (values) => {
          await saveProfile(db, values);
          onNext();
        })}
      >
        <Field label="Your name" error={errors.full_name?.message}>
          <Input autoComplete="name" {...form.register('full_name')} />
        </Field>
        <Field label="Date of birth (optional)" error={errors.dob?.message}>
          <Input type="date" {...form.register('dob')} />
        </Field>
        <div className="flex flex-col gap-2 pt-2">
          <Button type="submit" disabled={isSubmitting}>
            Continue
          </Button>
          <SkipButton onClick={onNext} />
          <SkipButton onClick={onSkipAll} label="Set up later" />
        </div>
      </form>
    </StepShell>
  );
}

function ScheduleStep({ profile, onNext }: { profile: Profile | null; onNext: () => void }) {
  const { current, isLoading } = useSchedules();
  if (isLoading) return null;
  return (
    <StepShell step={1} title="Your dialysis" intro="Your usual schedule, so RenalFlow can show your next session and spot missed ones.">
      <ScheduleForm current={current} modality={profile?.modality ?? null} submitLabel="Continue" onSaved={onNext} secondary={<SkipButton onClick={onNext} />} />
    </StepShell>
  );
}

const fluidSchema = z.object({
  dry_weight_kg: optionalNumber(20, 300, 'Enter a weight between 20 and 300 kg.'),
  fluid_limit_ml: optionalNumber(100, 5000, 'Enter an amount between 100 and 5000 mL.')
});

function FluidStep({ profile, fluidLimit, onNext }: { profile: Profile | null; fluidLimit: number | null; onNext: () => void }) {
  const db = useDb();
  const form = useForm<z.input<typeof fluidSchema>, unknown, z.output<typeof fluidSchema>>({
    resolver: zodResolver(fluidSchema),
    defaultValues: { dry_weight_kg: inputValue(profile?.dry_weight_kg), fluid_limit_ml: inputValue(fluidLimit) }
  });
  const { errors, isSubmitting } = form.formState;
  return (
    <StepShell step={2} title="Fluid & weight" intro="Use the numbers your care team gave you. If you don’t know them yet, skip this step.">
      <form
        noValidate
        className="space-y-4"
        onSubmit={form.handleSubmit(async (values) => {
          await saveProfile(db, { dry_weight_kg: values.dry_weight_kg });
          await saveSetting(db, 'fluid_limit_ml', values.fluid_limit_ml);
          onNext();
        })}
      >
        <Field label="Dry weight (kg)" help="Your target weight after dialysis." error={errors.dry_weight_kg?.message}>
          <Input inputMode="decimal" {...form.register('dry_weight_kg')} />
        </Field>
        <Field label="Daily fluid limit (mL)" help="Everything you drink in a day, including tea and soup." error={errors.fluid_limit_ml?.message}>
          <Input inputMode="numeric" {...form.register('fluid_limit_ml')} />
        </Field>
        <div className="flex flex-col gap-2 pt-2">
          <Button type="submit" disabled={isSubmitting}>
            Continue
          </Button>
          <SkipButton onClick={onNext} />
        </div>
      </form>
    </StepShell>
  );
}

const accessSchema = z.object({
  access_type: z.enum(['avf', 'avg', 'catheter']).nullable(),
  access_location: optionalText(120),
  access_created_on: optionalPastDate
});

function AccessStep({ profile, onNext }: { profile: Profile | null; onNext: () => void }) {
  const db = useDb();
  const form = useForm<z.input<typeof accessSchema>, unknown, z.output<typeof accessSchema>>({
    resolver: zodResolver(accessSchema),
    defaultValues: {
      access_type: profile?.access_type ?? null,
      access_location: inputValue(profile?.access_location),
      access_created_on: inputValue(profile?.access_created_on)
    }
  });
  const { errors, isSubmitting } = form.formState;
  return (
    <StepShell step={3} title="Your access" intro="The access used for dialysis. It helps you keep track of checks and problems.">
      <form
        noValidate
        className="space-y-4"
        onSubmit={form.handleSubmit(async (values) => {
          await saveProfile(db, values);
          onNext();
        })}
      >
        <Controller
          control={form.control}
          name="access_type"
          render={({ field }) => (
            <ChoiceGroup<AccessType>
              name="access_type"
              value={field.value ?? null}
              onChange={field.onChange}
              columns={3}
              options={(Object.keys(ACCESS_LABELS) as AccessType[]).map((value) => ({ value, label: ACCESS_LABELS[value] }))}
            />
          )}
        />
        <Field label="Where is it? (optional)" error={errors.access_location?.message}>
          <Input {...form.register('access_location')} placeholder="e.g. Left forearm" />
        </Field>
        <Field label="Created on (optional)" error={errors.access_created_on?.message}>
          <Input type="date" {...form.register('access_created_on')} />
        </Field>
        <div className="flex flex-col gap-2 pt-2">
          <Button type="submit" disabled={isSubmitting}>
            Finish
          </Button>
          <SkipButton onClick={onNext} />
        </div>
      </form>
    </StepShell>
  );
}

/** First-run setup (SPEC.md §5). Every step saves as it goes and can be skipped. */
export function OnboardingFlow() {
  const db = useDb();
  const { profile, isLoading: profileLoading } = useProfile();
  const { settings, isLoading: settingsLoading } = useSettings();
  const [step, setStep] = useState(0);
  const [finishing, setFinishing] = useState(false);

  async function finish() {
    setFinishing(true);
    await saveSetting(db, 'onboarding_done_at', new Date().toISOString());
  }

  if (profileLoading || settingsLoading) return null;

  if (step === 0) return <NameStep profile={profile} onNext={() => setStep(1)} onSkipAll={() => void finish()} />;
  if (step === 1) return <ScheduleStep profile={profile} onNext={() => setStep(2)} />;
  if (step === 2) return <FluidStep profile={profile} fluidLimit={settings.fluid_limit_ml} onNext={() => setStep(3)} />;
  if (step === 3) return <AccessStep profile={profile} onNext={() => setStep(4)} />;

  return (
    <div className="safe-top flex min-h-dvh items-center justify-center px-6 text-center">
      <div className="max-w-sm space-y-4">
        <CheckCircle2 className="mx-auto h-12 w-12 text-clinical-primary" />
        <h1 className="text-2xl font-semibold tracking-tight">You’re all set{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}</h1>
        <p className="text-sm text-clinical-muted">You can change any of this later under More → Profile and Settings.</p>
        <Button className="w-full" disabled={finishing} onClick={() => void finish()}>
          Go to Today
        </Button>
      </div>
    </div>
  );
}
